import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Menu, Search, Settings, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { searchAdminGlobal } from "../../../services/api";
import { useCurrencyFormatter } from "../../../utils/formatCurrency";
import { CurrencyContext } from "../../../context/CurrencyContext";
import "./Topbar.css";

const SEARCH_GROUPS = [
  { key: "users", targetMenu: "USERS", titleKey: "admin.globalSearch.groups.users" },
  {
    key: "sales",
    targetMenu: "SALES_REPORTS",
    titleKey: "admin.globalSearch.groups.sales",
  },
  {
    key: "sessions",
    targetMenu: "SESSIONS",
    titleKey: "admin.globalSearch.groups.sessions",
  },
  {
    key: "products",
    targetMenu: "SALES_REPORTS",
    titleKey: "admin.globalSearch.groups.products",
  },
  {
    key: "outlets",
    targetMenu: "OUTLET_RADIUS",
    titleKey: "admin.globalSearch.groups.outlets",
  },
  {
    key: "display_proofs",
    targetMenu: "DISPLAY_PROOFS",
    titleKey: "admin.globalSearch.groups.displayProofs",
  },
];

const RECENT_SEARCH_STORAGE_KEY = "admin_global_recent_searches";
const MAX_RECENT_SEARCHES = 5;
const MIN_QUERY_LENGTH = 2;

const safeString = (value) => String(value || "").trim();

const readRecentSearches = () => {
  try {
    const raw = localStorage.getItem(RECENT_SEARCH_STORAGE_KEY);
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed) ? parsed.filter((item) => safeString(item)) : [];
  } catch (_) {
    return [];
  }
};

const writeRecentSearches = (items) => {
  try {
    localStorage.setItem(RECENT_SEARCH_STORAGE_KEY, JSON.stringify(items));
  } catch (_) {
    // Ignore localStorage write failures.
  }
};

const upsertRecentSearch = (current, value) => {
  const normalized = safeString(value);
  if (!normalized) return current;

  const next = [normalized, ...current.filter((entry) => entry !== normalized)];
  return next.slice(0, MAX_RECENT_SEARCHES);
};

const formatResultDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-GB");
};

const getPrimaryText = (scope, item) => {
  if (!item) return "";

  if (scope === "users") return item.label || item.email || "Unknown user";
  if (scope === "sales") return item.promoter_name || "Sale";
  if (scope === "sessions") return item.promoter_name || "Session";
  if (scope === "products") return item.label || item.product_code || "Product";
  if (scope === "outlets") return item.label || item.name || "Outlet";
  if (scope === "display_proofs") return item.promoter_name || "Display Proof";
  return "";
};

const getSecondaryText = (scope, item, t, formatCurrency) => {
  if (!item) return "";

  if (scope === "users") {
    const role = safeString(item.role);
    const email = safeString(item.email);
    return [email, role].filter(Boolean).join(" • ");
  }

  if (scope === "sales") {
    const date = formatResultDate(item.sale_date);
    return [
      safeString(item.outlet_name),
      formatCurrency(item.total_amount, item.sale_date, item.currency),
      date,
    ]
      .filter(Boolean)
      .join(" • ");
  }

  if (scope === "sessions") {
    const status =
      item.status === "active"
        ? t("admin.dashboard.activeStatus")
        : t("admin.dashboard.checkedOut");
    return [safeString(item.outlet_name), status].filter(Boolean).join(" • ");
  }

  if (scope === "products") {
    const code = safeString(item.product_code);
    const category = safeString(item.category);
    return [category, code].filter(Boolean).join(" • ");
  }

  if (scope === "outlets") {
    return safeString(item.code);
  }

  if (scope === "display_proofs") {
    const type = safeString(item.proof_type).replace(/_/g, " ");
    const submittedDate = formatResultDate(item.submitted_at);
    return [safeString(item.outlet_name), type, submittedDate]
      .filter(Boolean)
      .join(" • ");
  }

  return "";
};

const getEntryQuery = (scope, item, fallbackQuery) => {
  const query = safeString(fallbackQuery);
  if (!item) return query;

  if (scope === "users") {
    return safeString(item.label || item.email || query);
  }

  if (scope === "sales") {
    return safeString(item.promoter_name || item.outlet_name || query);
  }

  if (scope === "sessions") {
    return safeString(item.promoter_name || item.outlet_name || query);
  }

  if (scope === "products") {
    return safeString(item.label || item.product_code || query);
  }

  if (scope === "outlets") {
    return safeString(item.label || item.code || query);
  }

  if (scope === "display_proofs") {
    return safeString(item.promoter_name || item.outlet_name || query);
  }

  return query;
};

export default function Topbar({
  searchQuery,
  setSearchQuery,
  onSearchNavigate,
  onToggleSidebar,
}) {
  const { t, i18n } = useTranslation();
  const { format: formatCurrency } = useCurrencyFormatter();
  const { currency, setCurrency } = useContext(CurrencyContext);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showSearchMenu, setShowSearchMenu] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [recentSearches, setRecentSearches] = useState(() => readRecentSearches());
  const [activeEntryIndex, setActiveEntryIndex] = useState(-1);
  const settingsMenuRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const activeRequestRef = useRef(null);

  const trimmedSearchQuery = safeString(searchQuery);
  const isQueryShort =
    trimmedSearchQuery.length > 0 && trimmedSearchQuery.length < MIN_QUERY_LENGTH;

  const groupedEntries = useMemo(() => {
    const groups = searchResult?.groups || {};
    const hasMoreByGroup = searchResult?.hasMore || {};

    return SEARCH_GROUPS.map((group) => {
      const items = Array.isArray(groups[group.key]) ? groups[group.key] : [];
      const includeViewAll = items.length > 0 || Boolean(hasMoreByGroup[group.key]);
      return {
        ...group,
        items,
        includeViewAll,
      };
    }).filter((group) => group.items.length > 0 || group.includeViewAll);
  }, [searchResult]);

  const interactiveEntries = useMemo(() => {
    const entries = [];
    groupedEntries.forEach((group) => {
      group.items.forEach((item, index) => {
        entries.push({
          type: "item",
          scope: group.key,
          targetMenu: group.targetMenu,
          item,
          indexInGroup: index,
        });
      });

      if (group.includeViewAll) {
        entries.push({
          type: "view_all",
          scope: group.key,
          targetMenu: group.targetMenu,
          item: null,
          indexInGroup: -1,
        });
      }
    });
    return entries;
  }, [groupedEntries]);

  const handleLanguageChange = (nextLanguage) => {
    i18n.changeLanguage(nextLanguage);
    localStorage.setItem("app_language", nextLanguage);
    setShowSettingsMenu(false);
  };

  useEffect(() => {
    if (!showSettingsMenu && !showSearchMenu) return undefined;

    const handleClickOutside = (event) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target)) {
        setShowSettingsMenu(false);
      }
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
        setShowSearchMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchMenu, showSettingsMenu]);

  useEffect(() => {
    const handleGlobalShortcut = (event) => {
      const isShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
      if (!isShortcut) return;

      event.preventDefault();
      setShowSearchMenu(true);
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    };

    document.addEventListener("keydown", handleGlobalShortcut);
    return () => {
      document.removeEventListener("keydown", handleGlobalShortcut);
    };
  }, []);

  useEffect(() => {
    if (!showSearchMenu) {
      setActiveEntryIndex(-1);
    }
  }, [showSearchMenu]);

  useEffect(
    () => () => {
      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
      }
    },
    [],
  );

  useEffect(() => {
    if (!showSearchMenu) return undefined;
    if (!trimmedSearchQuery) {
      setSearchResult(null);
      setSearchError("");
      setSearchLoading(false);
      return undefined;
    }
    if (isQueryShort) {
      setSearchResult(null);
      setSearchError("");
      setSearchLoading(false);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      try {
        if (activeRequestRef.current) {
          activeRequestRef.current.abort();
        }

        const controller = new AbortController();
        activeRequestRef.current = controller;
        setSearchLoading(true);
        setSearchError("");
        const response = await searchAdminGlobal({
          q: trimmedSearchQuery,
          scope: "all",
          limit: 5,
          signal: controller.signal,
        });
        setSearchResult(response?.data || null);
      } catch (error) {
        if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
          return;
        }
        setSearchResult(null);
        setSearchError(t("admin.globalSearch.fetchFailed"));
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isQueryShort, showSearchMenu, t, trimmedSearchQuery]);

  useEffect(() => {
    if (interactiveEntries.length === 0) {
      setActiveEntryIndex(-1);
      return;
    }
    setActiveEntryIndex((prev) => {
      if (prev < 0) return 0;
      if (prev >= interactiveEntries.length) return interactiveEntries.length - 1;
      return prev;
    });
  }, [interactiveEntries]);

  const rememberSearch = (value) => {
    setRecentSearches((prev) => {
      const next = upsertRecentSearch(prev, value);
      writeRecentSearches(next);
      return next;
    });
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    writeRecentSearches([]);
  };

  const applySearchSelection = ({
    scope,
    targetMenu,
    item,
    query,
    closeMenu = true,
    navigationType = "item",
  }) => {
    const nextQuery = getEntryQuery(scope, item, query);
    setSearchQuery(nextQuery);
    rememberSearch(nextQuery);
    if (closeMenu) {
      setShowSearchMenu(false);
    }
    if (typeof onSearchNavigate === "function") {
      onSearchNavigate({
        scope,
        targetMenu,
        query: nextQuery,
        item: item || null,
        navigationType,
      });
    }
  };

  const onSearchInputKeyDown = (event) => {
    if (!showSearchMenu) {
      if (event.key === "ArrowDown" && interactiveEntries.length > 0) {
        event.preventDefault();
        setShowSearchMenu(true);
      }
      return;
    }

    if (event.key === "Escape") {
      setShowSearchMenu(false);
      return;
    }

    if (event.key === "ArrowDown") {
      if (interactiveEntries.length === 0) return;
      event.preventDefault();
      setActiveEntryIndex((prev) =>
        prev < 0 ? 0 : (prev + 1) % interactiveEntries.length,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      if (interactiveEntries.length === 0) return;
      event.preventDefault();
      setActiveEntryIndex((prev) =>
        prev < 0
          ? interactiveEntries.length - 1
          : (prev - 1 + interactiveEntries.length) % interactiveEntries.length,
      );
      return;
    }

    if (event.key === "Enter") {
      if (activeEntryIndex < 0 || activeEntryIndex >= interactiveEntries.length) {
        return;
      }
      event.preventDefault();
      const selected = interactiveEntries[activeEntryIndex];
      applySearchSelection({
        scope: selected.scope,
        targetMenu: selected.targetMenu,
        item: selected.item,
        query: trimmedSearchQuery,
      });
    }
  };

  const renderSearchResults = () => {
    if (!trimmedSearchQuery) {
      return (
        <div className="admin-global-search-empty-state">
          <p className="admin-global-search-hint">
            {t("admin.globalSearch.emptyPrompt")}
          </p>
          {recentSearches.length > 0 && (
            <div className="admin-global-search-recent">
              <p className="admin-global-search-recent-title">
                {t("admin.globalSearch.recent")}
              </p>
              <div className="admin-global-search-recent-list">
                {recentSearches.map((entry) => (
                  <button
                    type="button"
                    key={entry}
                    className="admin-global-search-recent-item"
                    onClick={() =>
                      applySearchSelection({
                        scope: "sales",
                        targetMenu: "SALES_REPORTS",
                        item: null,
                        query: entry,
                        closeMenu: true,
                        navigationType: "recent",
                      })
                    }
                  >
                    {entry}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="admin-global-search-recent-clear-btn"
                onClick={clearRecentSearches}
              >
                {t("admin.globalSearch.clearRecent")}
              </button>
            </div>
          )}
        </div>
      );
    }

    if (isQueryShort) {
      return (
        <div className="admin-global-search-empty-state">
          <p>{t("admin.globalSearch.minChars", { count: MIN_QUERY_LENGTH })}</p>
        </div>
      );
    }

    if (searchLoading) {
      return (
        <div className="admin-global-search-empty-state">
          <p>{t("admin.common.loading")}</p>
        </div>
      );
    }

    if (searchError) {
      return (
        <div className="admin-global-search-empty-state admin-global-search-error">
          <p>{searchError}</p>
        </div>
      );
    }

    if (!groupedEntries.length) {
      return (
        <div className="admin-global-search-empty-state">
          <p>{t("admin.globalSearch.noMatches")}</p>
        </div>
      );
    }

    let interactiveIndex = -1;
    return groupedEntries.map((group) => (
      <div key={group.key} className="admin-global-search-group">
        <div className="admin-global-search-group-title">
          {t(group.titleKey)}
        </div>

        {group.items.map((item) => {
          interactiveIndex += 1;
          const rowIndex = interactiveIndex;
          const isActive = rowIndex === activeEntryIndex;
          return (
            <button
              key={`${group.key}-${item.id}`}
              type="button"
              className={`admin-global-search-item ${isActive ? "active" : ""}`}
              onMouseEnter={() => setActiveEntryIndex(rowIndex)}
              onClick={() =>
                applySearchSelection({
                  scope: group.key,
                  targetMenu: group.targetMenu,
                  item,
                  query: trimmedSearchQuery,
                  navigationType: "item",
                })
              }
            >
              <span className="admin-global-search-item-primary">
                {getPrimaryText(group.key, item)}
              </span>
              <span className="admin-global-search-item-secondary">
                {getSecondaryText(group.key, item, t, formatCurrency)}
              </span>
            </button>
          );
        })}

        {group.includeViewAll && (
          (() => {
            interactiveIndex += 1;
            const rowIndex = interactiveIndex;
            const isActive = rowIndex === activeEntryIndex;
            return (
              <button
                type="button"
                className={`admin-global-search-view-all ${isActive ? "active" : ""}`}
                onMouseEnter={() => setActiveEntryIndex(rowIndex)}
                onClick={() =>
                  applySearchSelection({
                    scope: group.key,
                    targetMenu: group.targetMenu,
                    item: null,
                    query: trimmedSearchQuery,
                    navigationType: "view_all",
                  })
                }
              >
                {t("admin.globalSearch.viewAll", { group: t(group.titleKey) })}
              </button>
            );
          })()
        )}
      </div>
    ));
  };

  return (
    <header className="admin-topbar">
      <button
        type="button"
        className="admin-sidebar-toggle"
        aria-label={t("admin.sidebar.toggle", { defaultValue: "Toggle sidebar" })}
        onClick={onToggleSidebar}
      >
        <Menu size={24} />
      </button>

      <div className="admin-search-container" ref={searchWrapperRef}>
        <div className="admin-search-field">
          <Search size={20} className="admin-search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t("admin.globalSearch.placeholder")}
            className="admin-search-input"
            value={searchQuery}
            onFocus={() => setShowSearchMenu(true)}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (!showSearchMenu) setShowSearchMenu(true);
            }}
            onKeyDown={onSearchInputKeyDown}
          />
          {searchQuery && (
            <button
              type="button"
              className="admin-search-clear"
              aria-label={t("admin.globalSearch.clearInput")}
              onClick={() => {
                setSearchQuery("");
                setShowSearchMenu(true);
                searchInputRef.current?.focus();
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {showSearchMenu && (
          <div className="admin-global-search-dropdown">
            <div className="admin-global-search-content">{renderSearchResults()}</div>
            <div className="admin-global-search-footer">
              <span>{t("admin.globalSearch.shortcutHint")}</span>
              <span>{t("admin.globalSearch.scopeHint")}</span>
            </div>
          </div>
        )}
      </div>

      <div className="admin-topbar-actions">
        <div className="admin-settings-wrapper" ref={settingsMenuRef}>
          <button
            className="admin-icon-btn"
            aria-label={t("admin.common.settings")}
            onClick={() => setShowSettingsMenu((prev) => !prev)}
          >
            <Settings size={20} />
          </button>

          {showSettingsMenu && (
            <div className="admin-settings-menu">
              <p className="admin-settings-title">{t("admin.common.language")}</p>
              <div className="admin-language-options">
                <button
                  type="button"
                  className={`admin-language-option ${i18n.language === "en" ? "active" : ""}`}
                  onClick={() => handleLanguageChange("en")}
                >
                  EN
                </button>
                <button
                  type="button"
                  className={`admin-language-option ${i18n.language === "ms" ? "active" : ""}`}
                  onClick={() => handleLanguageChange("ms")}
                >
                  BM
                </button>
              </div>
              <p className="admin-settings-title">{t("admin.common.currency")}</p>
              <div className="admin-language-options">
                <button
                  type="button"
                  className={`admin-language-option ${currency === "MYR" ? "active" : ""}`}
                  onClick={() => setCurrency("MYR")}
                >
                  MYR
                </button>
                <button
                  type="button"
                  className={`admin-language-option ${currency === "SGD" ? "active" : ""}`}
                  onClick={() => setCurrency("SGD")}
                >
                  SGD
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
